import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "inner" }
const _hoisted_2 = { class: "options" }
const _hoisted_3 = { class: "switch-subtitle" }
const _hoisted_4 = { class: "switch-subtitle" }
const _hoisted_5 = { class: "tags-pane" }
const _hoisted_6 = { class: "actions" }
const _hoisted_7 = {
  key: 0,
  class: "progress-bar"
}

import { computed, ref, useTemplateRef, createApp, defineAsyncComponent } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import Modal from '@components/modal/Modal.vue';
import AlbumPicker from '@components/modal/AlbumPicker.vue';
import EditTags from '@components/modal/EditTags.vue';
import UploadMenuItem from '@components/header/UploadMenuItem.vue';

import NcButton from '@nextcloud/vue/components/NcButton';
import NcTextField from '@nextcloud/vue/components/NcTextField';
import axios from '@nextcloud/axios';
import { showError } from '@services/utils/dialog';

import { useModal } from '@services/modal';
import { t, n } from '@services/l10n';
import { config } from '@services/user-config';
import { useRouteIs } from '@services/route-is';
import * as dav from '@services/dav';
import * as utils from '@services/utils/common';
import * as nativex from '@native';
import { API } from '@services/API';

import type { IAlbum, IPhoto, IUploadNativeX } from '@typings';
import type PCancelable from 'p-cancelable';


export default /*@__PURE__*/_defineComponent({
  ...{
  name: 'UploadModal',
},
  __name: 'UploadModal',
  setup(__props) {

const NcProgressBar = defineAsyncComponent(() => import('@nextcloud/vue/components/NcProgressBar'));
const NcCheckboxRadioSwitch = defineAsyncComponent(() => import('@nextcloud/vue/components/NcCheckboxRadioSwitch'));



const router = useRouter();
const route = useRoute();
const routeIs = useRouteIs();
const modal = useTemplateRef('modal');
const { show, close } = useModal(modal);
const tags = useTemplateRef<InstanceType<typeof EditTags>>('tags');

const files = ref<File[]>([]);
const pane = ref<0 | 1>(0);
const uploadPath = ref('/');
const albums = ref<IAlbum[]>([]);
const tagsShown = ref(false);
const processing = ref(false);
const progress = ref(0);
const progressNote = ref(String());
const currentUpload = ref<null | PCancelable<any>>(null);
const locals = ref<IUploadNativeX[]>([]);

const fileCount = computed(() => files.value.length + locals.value.length);

const albumNames = computed(() => {
  if (!albums.value.length) {
    return t('memories', 'No albums selected');
  }

  return albums.value.map((album) => album.name).join(', ');
});

console.assert(!_m.modals.upload, 'UploadModal created twice');
_m.modals.upload = open;

// create right header button
const header = document.querySelector<HTMLDivElement>('.header-right, .header-end');
if (header && utils.uid) {
  const div = document.createElement('div');
  header.prepend(div);
  const headerApp = createApp(UploadMenuItem);
  // Share router with header button
  try {
    headerApp.use(router);
  } catch {}
  headerApp.mount(div);
}

function open(localsIn?: IUploadNativeX[]) {
  // cannot upload to public shares
  if (routeIs.Public) return;

  // Upload local files natively (NativeX)
  if (localsIn?.length) {
    resetState();
    locals.value = localsIn;
    show.value = true;
    return;
  }

  // reset everything
  resetState();

  // prompt the user to select the files
  const input = document.createElement('input');
  input.type = 'file';
  input.multiple = true;
  input.accept = 'image/*,image/heic,image/tiff,video/*';
  input.addEventListener('cancel', () => input.remove());
  input.addEventListener('change', () => {
    files.value = Array.from(input.files ?? []);
    show.value = !!files.value.length;
    input.remove();
  });
  input.click();
}

function resetState() {
  pane.value = 0;
  files.value = [];
  albums.value = [];
  tagsShown.value = false;
  processing.value = false;
  progress.value = 0;
  progressNote.value = String();
  locals.value = [];

  // choose first path of timeline path
  uploadPath.value = config.timeline_path.split(';')?.[0] ?? '/';

  // choose current folder if in folders view
  if (routeIs.Folders) {
    uploadPath.value = utils.getFolderRoutePath(config.folders_path, { route });
  }
}

function cleanup() {
  show.value = false;
  files.value = [];
  locals.value = [];
  processing.value = false;
  currentUpload.value?.cancel('Modal closed');
}

async function chooseUploadPath() {
  try {
    const prompt = t('memories', 'Choose the destination folder for the upload');
    const selected = await utils.chooseNcFolder(prompt, uploadPath.value);
    uploadPath.value = selected || uploadPath.value;
  } catch (e) {
    console.log('chooseNcFolder cancelled', e);
  }
}

function selectAlbums(selection: IAlbum[]) {
  albums.value = selection;
  pane.value = 0;
}

async function upload() {
  try {
    processing.value = true;
    progress.value = 0;
    await uploadI();
    close();
  } catch {
    // do not quit
  } finally {
    processing.value = false;
    progressNote.value = String();
  }
}

async function uploadI() {
  // Tags may be created which might throw
  let tagsArr: number[] = [];
  if (tagsShown.value) {
    try {
      progressNote.value = t('memories', 'Creating tags');
      tagsArr = (await tags.value?.result?.())?.add ?? [];
    } catch (e: any) {
      showError(e);
      console.error(e);
      throw e;
    }
  }

  type UploadSource = { kind: 'file'; file: File } | { kind: 'nativex'; entry: IUploadNativeX };
  const queue: UploadSource[] = [
    ...files.value.map((file) => ({ kind: 'file' as const, file })),
    ...locals.value.map((entry) => ({ kind: 'nativex' as const, entry })),
  ];

  /**
   * for each file:
   *   upload
   *   for each album:
   *     add to album
   *   attach tags
   * Each operation is 100KB overhead
   */
  const OP_FAC = 100 * 1024;
  let maxProgress = files.value.reduce((sum, file) => sum + file.size, 0); // file size
  maxProgress += locals.value.length; // local size unknown, assume 1 each
  maxProgress += (tagsArr.length ? 1 : 0) * queue.length * OP_FAC; // tags
  maxProgress += albums.value.length * queue.length * OP_FAC; // albums

  // Update progress bar
  let progressLocal = 0;
  const addProgress = (delta: number) => {
    progressLocal += delta;
    progress.value = maxProgress ? (progressLocal * 100) / maxProgress : 0;
  };

  // Guard against closed modal
  const guardOpen = () => {
    if (!show.value) throw new Error('Modal closed');
  };

  // List of successful uploads
  const uploaded = [] as {
    fileid: number;
    filename: string;
    name: string;
  }[];

  // Sources that still need (re)trying after a partial failure
  const remaining = [] as UploadSource[];

  // Start upload process
  const { getUploader } = await import('@nextcloud/upload');
  const uploader = getUploader();
  for (const source of queue) {
    guardOpen();

    // add slash to upload path
    let path = uploadPath.value;
    if (!path.endsWith('/')) path += '/';

    if (source.kind === 'nativex') {
      // NativeX files upload natively without downloading
      try {
        const dest = path + source.entry.filename;
        progressNote.value = t('memories', 'Uploading {file}', { file: source.entry.filename });
        const fileid = await uploadNativeX(source.entry.auid, dest);
        guardOpen();
        uploaded.push({ fileid, filename: dest, name: source.entry.filename });
      } catch (e) {
        showError(t('memories', 'Failed to upload {file}', { file: source.entry.filename }));
        console.error(e);
        remaining.push(source);
      } finally {
        addProgress(1);
      }
    } else {
      // Browser files are uploaded using the uploader.
      const file = source.file;
      try {
        progressNote.value = t('memories', 'Uploading {file}', { file: file.name });

        const filename = path + file.name;
        const promise = (currentUpload.value = uploader.upload(filename, file));
        const res = await promise;
        currentUpload.value = null;

        const fileid = parseInt(res.response?.headers?.['oc-fileid'] ?? 0);
        if (!fileid) throw new Error('No fileid header in response');

        uploaded.push({ fileid, filename, name: file.name });
      } catch (e) {
        showError(t('memories', 'Failed to upload {file}', { file: file.name }));
        console.error(e);
        remaining.push(source);
      } finally {
        currentUpload.value = null;
        addProgress(file.size);
      }
    }
  }

  // Make IPhoto types for album calls
  const photos = uploaded.map((f) => ({
    fileid: f.fileid,
    basename: f.name,
    imageInfo: {
      filename: f.filename, // prevent info calls (see dav/base.ts)
    },
  })) as IPhoto[];

  // Add files to albums
  for (const album of albums.value) {
    guardOpen();

    progressNote.value = t('memories', 'Adding files to album {album}', { album: album.name });
    for await (const fileIds of dav.addToAlbum(album.user, album.name, photos)) {
      addProgress(OP_FAC);
    }
  }

  // Attach tags
  if (tagsArr.length) {
    for (const photo of photos) {
      guardOpen();

      progressNote.value = t('memories', 'Attaching tags to {file}', { file: photo.basename! });
      try {
        await axios.patch<null>(API.TAG_SET(photo.fileid), { add: tagsArr });
      } catch (e) {
        showError(t('memories', 'Failed to attach tags to {file}', { file: photo.basename! }));
        console.error(e);
      } finally {
        addProgress(OP_FAC);
      }
    }
  }

  // Refresh if anything was uploaded
  if (uploaded.length) {
    utils.bus.emit('memories:timeline:soft-refresh', null);
  }

  // Throw if all files were not uploaded
  if (uploaded.length !== queue.length) {
    showError(t('memories', 'Some files have not been uploaded.'));
    files.value = remaining.filter((s): s is { kind: 'file'; file: File } => s.kind === 'file').map((s) => s.file);
    locals.value = remaining
      .filter((s): s is { kind: 'nativex'; entry: IUploadNativeX } => s.kind === 'nativex')
      .map((s) => s.entry);
    throw new Error('Some files have not been uploaded.');
  }
}

async function uploadNativeX(auid: string, filename: string): Promise<number> {
  const res = await fetch(nativex.NAPI.UPLOAD_LOCAL(auid, filename));
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const fileid = parseInt((await res.json())?.fileid ?? 0);
  if (!fileid) throw new Error('No fileid in response');
  return fileid;
}

return (_ctx: any,_cache: any) => {
  return (_unref(show))
    ? (_openBlock(), _createBlock(Modal, {
        key: 0,
        ref_key: "modal",
        ref: modal,
        onClose: cleanup,
        size: "normal",
        "can-close": pane.value === 0
      }, {
        title: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(n)('memories', 'Upload {n} file', 'Upload {n} files', fileCount.value, { n: fileCount.value })), 1)
        ]),
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            (pane.value === 0)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createElementVNode("div", null, [
                    _createVNode(_unref(NcTextField), {
                      label: _unref(t)('memories', 'Destination path'),
                      "label-visible": true,
                      modelValue: uploadPath.value,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((uploadPath).value = $event)),
                      onClick: chooseUploadPath,
                      readonly: ""
                    }, null, 8, ["label", "modelValue"])
                  ]),
                  _createElementVNode("div", _hoisted_2, [
                    (_unref(config).albums_enabled)
                      ? (_openBlock(), _createBlock(_unref(NcCheckboxRadioSwitch), {
                          key: 0,
                          "model-value": albums.value.length > 0,
                          disabled: processing.value,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (pane.value = 1))
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(t)('memories', 'Add to albums')) + " ", 1),
                            _cache[3] || (_cache[3] = _createElementVNode("br", null, null, -1)),
                            _createElementVNode("span", _hoisted_3, _toDisplayString(albumNames.value), 1)
                          ]),
                          _: 1
                        }, 8, ["model-value", "disabled"]))
                      : _createCommentVNode("", true),
                    _createVNode(_unref(NcCheckboxRadioSwitch), {
                      modelValue: tagsShown.value,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((tagsShown).value = $event)),
                      disabled: processing.value
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)('memories', 'Add tags')) + " ", 1),
                        _cache[4] || (_cache[4] = _createElementVNode("br", null, null, -1)),
                        _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(t)('memories', 'Attach collaborative tags to all uploads')), 1)
                      ]),
                      _: 1
                    }, 8, ["modelValue", "disabled"]),
                    _createElementVNode("div", _hoisted_5, [
                      (tagsShown.value)
                        ? (_openBlock(), _createBlock(EditTags, {
                            key: 0,
                            ref_key: "tags",
                            ref: tags,
                            photos: [],
                            disabled: processing.value
                          }, null, 8, ["disabled"]))
                        : _createCommentVNode("", true)
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_6, [
                    (processing.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                          _createTextVNode(_toDisplayString(progressNote.value) + " ", 1),
                          _createVNode(_unref(NcProgressBar), {
                            value: progress.value,
                            error: true
                          }, null, 8, ["value"])
                        ]))
                      : _createCommentVNode("", true),
                    _createVNode(_unref(NcButton), {
                      onClick: upload,
                      variant: "primary",
                      disabled: processing.value
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)('memories', 'Upload')), 1)
                      ]),
                      _: 1
                    }, 8, ["disabled"])
                  ])
                ], 64))
              : (pane.value === 1)
                ? (_openBlock(), _createBlock(AlbumPicker, {
                    key: 1,
                    photos: [],
                    "initial-selection": albums.value,
                    disabled: processing.value,
                    onSelect: selectAlbums
                  }, null, 8, ["initial-selection", "disabled"]))
                : _createCommentVNode("", true)
          ])
        ]),
        _: 1
      }, 8, ["can-close"]))
    : _createCommentVNode("", true)
}
}

})